import React, { useState } from 'react';
import { PageTracker } from '../../../components/PageTracker.tsx';

interface PackageInfo {
  name: string;
  current: string;
  latest: string;
  latestStable: string;
  wanted: string;
  stability: {
    currentIsStable: boolean;
    latestIsStable: boolean;
    hasNewerStable: boolean;
    hasPrerelease: boolean;
  };
  upToDate: boolean;
  upToDateStable: boolean;
}

interface Vulnerability {
  module_name: string;
  severity: string;
  title: string;
}

interface AnalysisReport {
  outdated: PackageInfo[];
  vulnerabilities: Vulnerability[];
  summary: {
    total: number;
    upToDate: number;
    upToDateStable: number;
    hasNewerStable: number;
    usingPrerelease: number;
    hasPrerelease: number;
  };
  fileType?: string;
}

function DependencyAnalyzer() {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<AnalysisReport | null>(null);
  const [selectedPackages, setSelectedPackages] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
  };

  const handlePackageSelection = (packageName: string, isChecked: boolean) => {
    setSelectedPackages(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(packageName);
      } else {
        newSet.delete(packageName);
      }
      return newSet;
    });
  };

  const handleSelectAll = (isChecked: boolean) => {
    if (isChecked && report?.outdated) {
      // Only select packages that are not up to date (can be upgraded)
      const upgradablePackages = report.outdated.filter(pkg => !pkg.upToDate);
      setSelectedPackages(new Set(upgradablePackages.map(pkg => pkg.name)));
    } else {
      setSelectedPackages(new Set());
    }
  };

  const exportUpdatedPackageJson = async () => {
    if (!file || !report || selectedPackages.size === 0) return;

    try {
      const originalContent = await file.text();
      let updatedContent = '';
      let filename = '';

      if (report.fileType === 'npm') {
        const packageJson = JSON.parse(originalContent);
        
        // Update selected packages to their latest versions
        selectedPackages.forEach(packageName => {
          const pkg = report.outdated.find(p => p.name === packageName);
          if (pkg) {
            // Update in dependencies
            if (packageJson.dependencies && packageJson.dependencies[packageName]) {
              packageJson.dependencies[packageName] = `^${pkg.latest}`;
            }
            // Update in devDependencies
            if (packageJson.devDependencies && packageJson.devDependencies[packageName]) {
              packageJson.devDependencies[packageName] = `^${pkg.latest}`;
            }
          }
        });
        
        updatedContent = JSON.stringify(packageJson, null, 2);
        filename = 'package-updated.json';
        
      } else if (report.fileType === 'gradle') {
        // For Gradle files, we'll create a simple text file with upgrade suggestions
        let gradleContent = originalContent;
        
        selectedPackages.forEach(packageName => {
          const pkg = report.outdated.find(p => p.name === packageName);
          if (pkg) {
            // Create a comment with upgrade suggestion
            const oldDep = `${pkg.name}:${pkg.current}`;
            const newDep = `${pkg.name}:${pkg.latest}`;
            gradleContent = gradleContent.replace(
              new RegExp(oldDep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
              `${newDep} // Updated from ${pkg.current}`
            );
          }
        });
        
        updatedContent = gradleContent;
        filename = 'build-updated.gradle';
        
      } else if (report.fileType === 'cocoapods') {
        // For Podfiles, we'll update the pod versions
        let podfileContent = originalContent;
        
        selectedPackages.forEach(packageName => {
          const pkg = report.outdated.find(p => p.name === packageName);
          if (pkg) {
            const oldPodLine = new RegExp(`pod\\s+['"]${pkg.name}['"](?:\\s*,\\s*['"][^'"]*['"])?`, 'g');
            podfileContent = podfileContent.replace(
              oldPodLine,
              `pod '${pkg.name}', '${pkg.latest}' # Updated from ${pkg.current}`
            );
          }
        });
        
        updatedContent = podfileContent;
        filename = 'Podfile-updated';
      }

      // Create and download the updated file
      const blob = new Blob([updatedContent], { 
        type: report.fileType === 'npm' ? 'application/json' : 'text/plain' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting file:', error);
      alert('Error exporting file. Please try again.');
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setReport(null); // Clear previous results
    setSelectedPackages(new Set()); // Clear previous selections

    try {
      const formData = new FormData();
      formData.append('package', file);

      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/analyze`, {
        method: 'POST',
        body: formData
      });

      const data = await res.json();
      setReport(data);
    } catch (error) {
      console.error('Error analyzing package:', error);
      alert('Error analyzing package. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
      <PageTracker pageTitle="Dependency Analyzer" category="dev-utils" />
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h1 style={{ color: '#333', marginBottom: '0.5rem' }}>📦 Dependency Risk Analyzer</h1>
        <p style={{ color: 'var(--app-text-2)', margin: 0 }}>Upload your dependency file to analyze current, stable, and latest versions</p>
        <p style={{ color: 'var(--lyra-color-text-muted)', margin: '0.5rem 0 0 0', fontSize: '0.9em' }}>
          Supports: package.json (Node.js), build.gradle (Android), Podfile (iOS)
        </p>
      </div>
      
      <div style={{ 
        marginBottom: '2rem', 
        padding: '1.5rem', 
        border: '2px dashed var(--lyra-color-border)', 
        borderRadius: '8px',
        textAlign: 'center',
        backgroundColor: 'var(--lyra-color-surface-2)'
      }}>
        <input 
          type="file" 
          accept=".json,.gradle,Podfile"
          onChange={handleFileChange}
          style={{
            marginBottom: '1rem',
            padding: '0.5rem',
            border: '1px solid var(--lyra-color-border)',
            borderRadius: '4px'
          }}
        />
        <br />
        <button 
          onClick={handleUpload}
          disabled={!file || isLoading}
          style={{
            padding: '12px 24px',
            backgroundColor: (!file || isLoading) ? 'var(--app-border-strong)' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: (!file || isLoading) ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            margin: '0 auto'
          }}
        >
          {isLoading ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid color-mix(in srgb, var(--lyra-color-surface) 19%, transparent)',
                borderTop: '2px solid var(--lyra-color-surface)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Analyzing...
            </>
          ) : (
            <>🔍 Analyze Dependencies</>
          )}
        </button>
      </div>

      {/* Add CSS animation for spinner */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {isLoading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem',
          backgroundColor: 'var(--lyra-color-surface-2)',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid var(--app-surface-3)',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            marginBottom: '1rem'
          }}></div>
          <h3 style={{ color: '#007bff', margin: '0 0 0.5rem 0' }}>Analyzing Dependencies...</h3>
          <p style={{ color: 'var(--app-text-2)', margin: 0, textAlign: 'center' }}>
            Running npm commands to check package versions and vulnerabilities.<br />
            This may take a moment.
          </p>
        </div>
      )}

      {report && (
        <div>
          {report.summary && (
            <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: 'var(--lyra-color-surface-2)', borderRadius: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h2 style={{ margin: 0 }}>📊 Summary</h2>
                {report.fileType && (
                  <div style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    borderRadius: '20px',
                    fontSize: '0.9em',
                    fontWeight: 'bold'
                  }}>
                    {report.fileType === 'npm' && '📦 NPM (package.json)'}
                    {report.fileType === 'gradle' && '🤖 Gradle (build.gradle)'}
                    {report.fileType === 'cocoapods' && '🍎 CocoaPods (Podfile)'}
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>📦 Total Packages: <strong>{report.summary.total}</strong></div>
                <div>✅ Up to Date: <strong>{report.summary.upToDate}</strong></div>
                <div>🔒 Up to Date (Stable): <strong>{report.summary.upToDateStable}</strong></div>
                <div>⬆️ Has Newer Stable: <strong>{report.summary.hasNewerStable}</strong></div>
                <div>🧪 Using Pre-release: <strong>{report.summary.usingPrerelease}</strong></div>
                <div>🚧 Has Pre-release Available: <strong>{report.summary.hasPrerelease}</strong></div>
              </div>
            </div>
          )}

          <h2>📦 Package Versions</h2>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--app-surface-3)' }}>
                  <th style={{ padding: '12px', textAlign: 'center', border: '1px solid var(--lyra-color-border)', width: '60px' }}>
                    <input
                      type="checkbox"
                      checked={report.outdated ? (() => {
                        const upgradablePackages = report.outdated.filter(pkg => !pkg.upToDate);
                        return upgradablePackages.length > 0 && upgradablePackages.every(pkg => selectedPackages.has(pkg.name));
                      })() : false}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Package</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Current</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Latest</th>
                  <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {report.outdated?.map((pkg) => {
                  const getStatusBadge = (pkg) => {
                    if (pkg.upToDate) return '✅ Up to Date';
                    if (pkg.upToDateStable) return '🔒 Stable Current';
                    if (pkg.stability?.hasNewerStable) return '⬆️ Stable Available';
                    if (!pkg.stability?.currentIsStable) return '🧪 Using Pre-release';
                    return '❓ Check Needed';
                  };

                  const getStatusColor = (pkg) => {
                    if (pkg.upToDate) return 'var(--app-surface-3)';
                    if (pkg.upToDateStable) return 'var(--app-primary-soft)';
                    if (pkg.stability?.hasNewerStable) return 'var(--lyra-color-warning-bg)';
                    if (!pkg.stability?.currentIsStable) return '#f8d7da';
                    return 'var(--lyra-color-surface-2)';
                  };

                  return (
                    <tr key={pkg.name} style={{ backgroundColor: getStatusColor(pkg) }}>
                      <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={selectedPackages.has(pkg.name)}
                          onChange={(e) => handlePackageSelection(pkg.name, e.target.checked)}
                          disabled={pkg.upToDate}
                          style={{ 
                            cursor: pkg.upToDate ? 'not-allowed' : 'pointer',
                            opacity: pkg.upToDate ? 0.5 : 1
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)', fontFamily: 'monospace' }}>
                        <strong>{pkg.name}</strong>
                      </td>
                      <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)', fontFamily: 'monospace' }}>
                        {pkg.current}
                        {!pkg.stability?.currentIsStable && <span style={{ color: '#856404' }}> (pre-release)</span>}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)', fontFamily: 'monospace' }}>
                        {pkg.latest}
                        {pkg.stability?.hasPrerelease && pkg.latest !== pkg.latestStable && (
                          <span style={{ color: '#6c757d', fontSize: '0.8em' }}> (pre-release)</span>
                        )}
                      </td>
                      <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)' }}>
                        {getStatusBadge(pkg)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {report.outdated && report.outdated.length > 0 && (
            <div style={{ 
              marginBottom: '2rem', 
              padding: '1rem', 
              backgroundColor: 'var(--lyra-color-surface-2)', 
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div>
                <strong>{selectedPackages.size}</strong> of <strong>{report.outdated.filter(pkg => !pkg.upToDate).length}</strong> upgradable packages selected
                {selectedPackages.size > 0 && (
                  <div style={{ fontSize: '0.9em', color: 'var(--app-text-2)', marginTop: '0.5rem' }}>
                    Selected: {Array.from(selectedPackages).join(', ')}
                  </div>
                )}
              </div>
              <button
                onClick={exportUpdatedPackageJson}
                disabled={selectedPackages.size === 0}
                style={{
                  padding: '12px 24px',
                  backgroundColor: selectedPackages.size > 0 ? '#28a745' : 'var(--app-border-strong)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedPackages.size > 0 ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                📦 Export Updated File
              </button>
            </div>
          )}

          <h2>🔐 Vulnerabilities</h2>
          {report.vulnerabilities && report.vulnerabilities.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8d7da' }}>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Package</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Severity</th>
                    <th style={{ padding: '12px', textAlign: 'left', border: '1px solid var(--lyra-color-border)' }}>Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {report.vulnerabilities.map((vuln, idx) => {
                    const getSeverityColor = (severity) => {
                      switch (severity?.toLowerCase()) {
                        case 'critical': return '#721c24';
                        case 'high': return '#856404';
                        case 'moderate': return '#0c5460';
                        case 'low': return '#155724';
                        default: return '#6c757d';
                      }
                    };

                    return (
                      <tr key={idx}>
                        <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)', fontFamily: 'monospace' }}>
                          {vuln.module_name}
                        </td>
                        <td style={{ 
                          padding: '12px', 
                          border: '1px solid var(--lyra-color-border)',
                          color: getSeverityColor(vuln.severity),
                          fontWeight: 'bold'
                        }}>
                          {vuln.severity?.toUpperCase()}
                        </td>
                        <td style={{ padding: '12px', border: '1px solid var(--lyra-color-border)' }}>
                          {vuln.title}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: '#155724', padding: '1rem', backgroundColor: 'var(--app-surface-3)', borderRadius: '4px' }}>
              ✅ No vulnerabilities found!
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default DependencyAnalyzer;
